/**
 * App Shell —— 依据 design/01-today-tablet.png。
 *
 * tablet-first：默认样式按 768–1024px 写（柜台平板是主力设备），
 * 向下降级为底部 Tab，向上加宽侧栏。
 */

import { useEffect, useState, type ReactNode } from 'react'
import { NavLink, useLocation, useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import {
  Bell,
  CalendarDays,
  HardDriveDownload,
  Calendar,
  ClipboardList,
  MoreHorizontal,
  Plus,
  Search,
  Settings,
  Users,
  Wallet,
  WifiOff,
} from 'lucide-react'
import { api, type Me } from '../lib/api'
import { Avatar, Banner, Button, cx } from '../components/ui'
import { GlobalSearch } from './GlobalSearch'
import { NewCaseDrawer } from '../features/NewCaseDrawer'

interface NavItem {
  to: string
  label: string
  icon: ReactNode
  ownerOnly?: boolean
  mobile?: boolean
}

/** 底部 Tab 只有 5 格，装不下的收进「更多」抽屉 */
const MORE: NavItem[] = [
  { to: '/finance', label: '账目', icon: <Wallet size={22} />, ownerOnly: true },
  { to: '/reminders', label: '提醒', icon: <Bell size={22} /> },
  { to: '/settings', label: '设置', icon: <Settings size={22} /> },
]

const NAV: NavItem[] = [
  { to: '/', label: '今天', icon: <CalendarDays size={22} />, mobile: true },
  { to: '/customers', label: '顾客', icon: <Users size={22} />, mobile: true },
  { to: '/cases', label: '工单', icon: <ClipboardList size={22} />, mobile: true },
  { to: '/calendar', label: '日历', icon: <Calendar size={22} />, mobile: true },
  { to: '/finance', label: '账目', icon: <Wallet size={22} />, ownerOnly: true },
  { to: '/reminders', label: '提醒', icon: <Bell size={22} /> },
  { to: '/settings', label: '设置', icon: <Settings size={22} /> },
]

export function Shell({ me, children }: { me: Me; children: ReactNode }) {
  const [searchOpen, setSearchOpen] = useState(false)
  const [newCaseOpen, setNewCaseOpen] = useState(false)
  const [moreOpen, setMoreOpen] = useState(false)
  const [online, setOnline] = useState(navigator.onLine)
  const location = useLocation()
  const navigate = useNavigate()

  /**
   * 多久没把备份取走。
   *
   * 【只有老板查】—— 助手管不了备份，提醒他只是噪音。
   * 一小时才重查一次：这是个以「天」为单位的事，不需要盯着。
   */
  const backupStatus = useQuery({
    queryKey: ['backup-status'],
    queryFn: () =>
      api.get<{ lastDownloadedAt: string | null; daysSince: number | null; overdue: boolean }>(
        '/api/backups/status',
      ),
    enabled: me.user.role === 'OWNER',
    staleTime: 60 * 60_000,
    retry: false,
  })

  const counts = useQuery({
    queryKey: ['reminder-counts'],
    queryFn: () =>
      api.get<{ manualRequired: number; pending: number; channelHealthy: boolean; channelConfigured: boolean }>(
        '/api/reminders/counts',
      ),
    refetchInterval: 60_000,
  })

  useEffect(() => {
    setMoreOpen(false)
  }, [location.pathname])

  useEffect(() => {
    const on = () => setOnline(true)
    const off = () => setOnline(false)
    window.addEventListener('online', on)
    window.addEventListener('offline', off)
    return () => {
      window.removeEventListener('online', on)
      window.removeEventListener('offline', off)
    }
  }, [])

  // 「/」聚焦搜索 —— 妈妈使用频率最高的入口
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const tag = (e.target as HTMLElement)?.tagName
      if (e.key === '/' && tag !== 'INPUT' && tag !== 'TEXTAREA') {
        e.preventDefault()
        setSearchOpen(true)
      }
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [])

  const items = NAV.filter((n) => !n.ownerOnly || me.user.role === 'OWNER')
  const moreActive = MORE.some((n) => location.pathname.startsWith(n.to))
  const manual = counts.data?.manualRequired ?? 0

  /**
   * 详情页不显示「开单」浮动按钮。
   *
   * 它是固定定位、飘在内容之上的，而工单详情的主动作「完成这一步」是页内按钮。
   * 滚到那个按钮进入浮动按钮那一带时，右边会被盖掉八十多像素 ——
   * 页面最重要的一次点击，被一个次要入口挡住。
   * 那两页各自有明确的主动作，不需要再飘一个。
   */
  const hideFab = /^\/(cases|customers)\/[^/]+$/.test(location.pathname)

  return (
    <div className="flex min-h-dvh flex-col">
      {/* 异常横条置于最顶部、跨越侧栏，确保被看见 */}
      {!online && (
        <Banner tone="warn" icon={<WifiOff size={20} />}>
          没有网络连接，暂时只能查看
        </Banner>
      )}
      {/* 这不是故障提示，是今天的待办 —— 手动发送是本店【选择】的工作方式。
          用红色告警来报正常流程，结果只会是这条横幅被彻底无视。 */}
      {/* 备份逾期。放在提醒横幅【之前】—— 数据没有异地副本，
          比今天有几个顾客没通知严重得多。 */}
      {online && backupStatus.data?.overdue && (
        <Banner
          tone="warn"
          icon={<HardDriveDownload size={20} />}
          action={
            <Button size="sm" variant="secondary" onClick={() => navigate('/settings')}>
              去备份
            </Button>
          }
        >
          {backupStatus.data.daysSince === null
            ? '还没有把备份下载到自己的电脑过'
            : `已经 ${backupStatus.data.daysSince} 天没下载备份了`}
        </Banner>
      )}

      {online && manual > 0 && (
        <Banner
          tone="info"
          icon={<Bell size={20} />}
          action={
            <Button size="sm" variant="secondary" onClick={() => navigate('/reminders')}>
              去通知
            </Button>
          }
        >
          今天有 {manual} 位顾客要通知
        </Banner>
      )}

      <div className="flex flex-1">
        {/* ── 侧栏（平板起）── */}
        <aside className="hidden shrink-0 border-r border-line bg-white md:flex md:w-50 xl:w-60 md:flex-col">
          <div className="px-5 py-5">
            <span className="text-[19px] font-bold tracking-tight">
              牙记 <span className="text-brand-600">GigiBook</span>
            </span>
          </div>
          <nav className="flex flex-col gap-0.5 px-2">
            {items.map((n) => (
              <SideLink
                key={n.to}
                item={n}
                badge={n.to === '/reminders' ? manual : 0}
                active={isActive(location.pathname, n.to)}
              />
            ))}
          </nav>
        </aside>

        <div className="flex min-w-0 flex-1 flex-col">
          {/* ── 顶栏 ── */}
          <header className="sticky top-0 z-20 flex h-16 shrink-0 items-center gap-3 border-b border-line bg-white px-4 sm:px-6">
            <button
              onClick={() => setSearchOpen(true)}
              className="flex h-12 flex-1 items-center gap-3 rounded-md border border-line-strong px-3.5 text-left text-[17px] text-ink-3 transition-colors hover:border-line-strong hover:bg-sunken md:max-w-105"
            >
              <Search size={20} className="shrink-0" />
              <span className="flex-1 truncate">搜索顾客 / 电话 / 工单号</span>
              <kbd className="hidden shrink-0 rounded border border-line px-1.5 py-0.5 text-[13px] md:block">
                /
              </kbd>
            </button>

            {/* ml-auto 把用户区推到最右：搜索框有 max-w，不推的话它会紧贴搜索框，右边空一大片 */}
            <div className="ml-auto hidden items-center gap-2 md:flex">
              <Avatar name={me.user.name} size={36} />
              <span className="text-[16px] font-semibold">{me.user.name}</span>
            </div>
          </header>

          {/* ── 内容区。底部留白避让 FAB 与 Tab 栏（DESIGN-SYSTEM §6.16）── */}
          <main className="flex-1 px-4 py-5 pb-26 sm:px-6 md:pb-20">{children}</main>
        </div>
      </div>

      {/* ── FAB：所有页面常驻 ── */}
      {!hideFab && (
        <Button
          variant="primary"
          size="lg"
          icon={<Plus size={22} />}
          onClick={() => setNewCaseOpen(true)}
          className="fixed right-4 bottom-22 z-30 rounded-full shadow-md md:right-6 md:bottom-6"
        >
          开单
        </Button>
      )}

      {/* ── 底部 Tab（手机）。pb-safe 避开 iPhone 底部横条 ── */}
      <nav className="fixed inset-x-0 bottom-0 z-20 flex h-18 border-t border-line bg-white pb-[env(safe-area-inset-bottom)] md:hidden">
        {NAV.filter((n) => n.mobile).map((n) => (
          <TabLink key={n.to} item={n} active={isActive(location.pathname, n.to)} />
        ))}
        <button
          onClick={() => setMoreOpen(true)}
          className={cx(
            'relative flex flex-1 flex-col items-center justify-center gap-1',
            moreActive ? 'text-brand-700' : 'text-ink-3',
          )}
        >
          <MoreHorizontal size={26} />
          <span className="text-[13px] font-medium">更多</span>
          {manual > 0 && (
            <span className="absolute right-1/4 top-2 size-2 rounded-full bg-brand-600" />
          )}
        </button>
      </nav>

      {/* 手机上账目/提醒/设置进不去 —— 底部 Tab 只有 5 格，剩下的收进这里 */}
      {moreOpen && (
        <div className="fixed inset-0 z-40 md:hidden" onClick={() => setMoreOpen(false)}>
          <div className="gb-anim-overlay absolute inset-0 bg-[rgb(28_25_23/0.4)]" />
          <nav
            onClick={(e) => e.stopPropagation()}
            className="gb-anim-toast absolute inset-x-0 bottom-0 rounded-t-lg bg-white pb-[calc(env(safe-area-inset-bottom)+12px)] shadow-lg"
          >
            <div className="mx-auto my-3 h-1 w-10 rounded-full bg-line-strong" />
            {MORE.filter((n) => !n.ownerOnly || me.user.role === 'OWNER').map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                onClick={() => setMoreOpen(false)}
                className="flex h-14 items-center gap-3 border-b border-line px-6 text-[18px] last:border-0"
              >
                {n.icon}
                <span className="flex-1">{n.label}</span>
                {n.to === '/reminders' && manual > 0 && (
                  <span className="tnum flex size-6 items-center justify-center rounded-full bg-bad text-[13px] font-bold text-white">
                    {manual}
                  </span>
                )}
              </NavLink>
            ))}
            <button
              onClick={() => setMoreOpen(false)}
              className="flex h-14 w-full items-center justify-center text-[17px] font-semibold text-ink-2"
            >
              关闭
            </button>
          </nav>
        </div>
      )}

      <GlobalSearch open={searchOpen} onClose={() => setSearchOpen(false)} />
      <NewCaseDrawer open={newCaseOpen} onClose={() => setNewCaseOpen(false)} />
    </div>
  )
}

function isActive(pathname: string, to: string): boolean {
  return to === '/' ? pathname === '/' : pathname.startsWith(to)
}

function SideLink({ item, active, badge }: { item: NavItem; active: boolean; badge: number }) {
  return (
    <NavLink
      to={item.to}
      className={cx(
        'relative flex h-12 items-center gap-3 rounded-md px-3 text-[17px] font-medium transition-colors',
        active
          ? 'bg-brand-100 font-semibold text-brand-700'
          : 'text-ink-2 hover:bg-sunken',
      )}
    >
      {active && (
        <span className="absolute left-0 top-2 bottom-2 w-[3px] rounded-full bg-brand-600" />
      )}
      {item.icon}
      <span className="flex-1">{item.label}</span>
      {badge > 0 && (
        <span className="tnum flex size-6 items-center justify-center rounded-full bg-bad text-[13px] font-bold text-white">
          {badge}
        </span>
      )}
    </NavLink>
  )
}

function TabLink({ item, active, dot }: { item: NavItem; active: boolean; dot?: boolean }) {
  return (
    <NavLink
      to={item.to}
      className={cx(
        'relative flex flex-1 flex-col items-center justify-center gap-1',
        active ? 'text-brand-700' : 'text-ink-3',
      )}
    >
      {/* 图标在 NAV 里是 22px（桌面侧栏刚好）。手机底部要更好点，
          在这里放大到 26，而不是改 NAV 常量把侧栏也撑大。 */}
      <span className="[&>svg]:size-6.5">{item.icon}</span>
      <span className="text-[13px] font-medium">{item.label}</span>
      {dot && <span className="absolute right-1/4 top-2 size-2 rounded-full bg-bad" />}
    </NavLink>
  )
}
